import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProfile } from "@/context/ProfileContext";
import { AVATAR_OPTIONS, avatarOptionFor } from "@/lib/profileStore";
import ProfileAvatar from "@/components/ProfileAvatar";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import { NotFound } from "@/pages/ErrorPages";
import { Plus, Trash2, X, Loader2, ArrowLeft } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const MAX_PROFILES = 5;

export default function ManageProfiles() {
  const { slug, sp } = useStore();
  const navigate = useNavigate();
  const { user, status } = useAuth();
  const { profiles, refreshProfiles, activeProfile, clearActiveProfile } = useProfile();
  const [tenant, setTenant] = useState(null);
  const [tenantStatus, setTenantStatus] = useState("loading");
  const [editing, setEditing] = useState(null); // {id?, name, avatar_key, is_kid, pin?, hasPin}
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/public/tenants/${slug}`)
      .then((r) => alive && (setTenant(r.data), setTenantStatus("ready")))
      .catch(() => alive && setTenantStatus("not_found"));
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => { refreshProfiles(); }, [refreshProfiles]);

  if (status === "loading" || tenantStatus === "loading") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] grid place-items-center text-white/40 text-xs uppercase tracking-widest" data-testid="manage-profiles-loading">
        Loading…
      </div>
    );
  }
  if (tenantStatus === "not_found" || !tenant) return <NotFound />;
  if (status !== "authenticated" || !user) {
    navigate(sp(`/login`), { replace: true });
    return null;
  }

  const startNew = () => {
    setError("");
    setEditing({ id: null, name: "", avatar_key: "violet", is_kid: false, pin: "", hasPin: false });
  };
  const startEdit = (p) => {
    setError("");
    setEditing({ id: p.id, name: p.name, avatar_key: p.avatar_key, is_kid: p.is_kid, pin: "", hasPin: p.has_pin });
  };
  const closeEdit = () => { setEditing(null); setError(""); };

  const submitEdit = async () => {
    if (!editing) return;
    setError("");
    if (!editing.name?.trim()) { setError("Name is required"); return; }
    if (editing.pin && !/^\d{4}$/.test(editing.pin)) { setError("PIN must be 4 digits"); return; }
    setSubmitting(true);
    try {
      if (editing.id) {
        // Update
        const body = {
          name: editing.name.trim(),
          avatar_key: editing.avatar_key,
          is_kid: editing.is_kid,
        };
        if (editing.pin && /^\d{4}$/.test(editing.pin)) body.pin = editing.pin;
        if (editing.clearPin) body.pin = "";
        await api.patch(`/me/profiles/${editing.id}`, body);
      } else {
        const body = {
          name: editing.name.trim(),
          avatar_key: editing.avatar_key,
          is_kid: editing.is_kid,
        };
        if (editing.pin && /^\d{4}$/.test(editing.pin)) body.pin = editing.pin;
        await api.post("/me/profiles", body);
      }
      await refreshProfiles();
      closeEdit();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!confirmDelete) return;
    setSubmitting(true);
    setError("");
    try {
      await api.delete(`/me/profiles/${confirmDelete.id}`);
      // If user deleted the active profile, clear it
      if (activeProfile?.id === confirmDelete.id) clearActiveProfile();
      await refreshProfiles();
      setConfirmDelete(null);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const themeColor = tenant.theme_color || "#7C3AED";
  const accentColor = tenant.accent_color || "#F59E0B";
  const canAdd = profiles.length < MAX_PROFILES;

  return (
    <StorefrontLayout tenant={tenant} currentUser={user}>
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16" data-testid="manage-profiles-page">
        <button
          type="button"
          onClick={() => navigate(sp(`/select-profile`))}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/55 hover:text-white"
          data-testid="manage-profiles-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to picker
        </button>
        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Profiles</p>
          <h1 className="mt-3 font-display font-black text-4xl md:text-5xl tracking-tighter">Manage profiles</h1>
          <p className="mt-3 text-white/60 text-sm md:text-base font-light max-w-xl">
            Up to 5 profiles per account. Kids profiles only see content rated U or U/A 7+.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
          {profiles.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => startEdit(p)}
              className="group flex flex-col items-center gap-3 p-4 border border-white/10 hover:border-white bg-black/30 transition-colors"
              data-testid={`manage-profile-card-${p.id}`}
            >
              <ProfileAvatar profile={p} size="lg" />
              <div className="text-center">
                <div className="text-sm font-medium tracking-tight">{p.name}</div>
                <div className="mt-0.5 text-[10px] uppercase tracking-[0.3em] text-white/40">
                  {p.is_kid ? "Kid · " : ""}{p.has_pin ? "PIN" : "Open"}
                </div>
              </div>
            </button>
          ))}
          {canAdd && (
            <button
              type="button"
              onClick={startNew}
              className="flex flex-col items-center justify-center gap-3 p-4 border border-dashed border-white/20 hover:border-white text-white/50 hover:text-white"
              data-testid="manage-profile-add"
            >
              <div className="h-24 w-24 grid place-items-center">
                <Plus className="w-10 h-10" strokeWidth={1.5} />
              </div>
              <div className="text-sm font-medium">Add profile</div>
            </button>
          )}
        </div>

        {!canAdd && (
          <div className="mt-6 text-[11px] uppercase tracking-[0.3em] text-white/45 text-center" data-testid="manage-profile-limit">
            5 profile maximum reached.
          </div>
        )}
      </div>

      {editing && (
        <ProfileEditor
          editing={editing}
          setEditing={setEditing}
          onClose={closeEdit}
          onSubmit={submitEdit}
          onDelete={editing.id ? () => setConfirmDelete(profiles.find((p) => p.id === editing.id)) : null}
          submitting={submitting}
          error={error}
          themeColor={themeColor}
          accentColor={accentColor}
          totalProfiles={profiles.length}
        />
      )}

      {confirmDelete && (
        <ConfirmDelete
          profile={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={onConfirmDelete}
          submitting={submitting}
        />
      )}
    </StorefrontLayout>
  );
}

function ProfileEditor({ editing, setEditing, onClose, onSubmit, onDelete, submitting, error, themeColor, accentColor, totalProfiles }) {
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/75 backdrop-blur-md p-4" onClick={onClose} data-testid="profile-editor-modal">
      <div className="relative w-full max-w-lg bg-[#0a0a0a] border border-white/10 p-7 md:p-9" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-white/55 hover:text-white grid place-items-center w-8 h-8" data-testid="profile-editor-close">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-4 mb-6">
          <ProfileAvatar avatarKey={editing.avatar_key} name={editing.name || "?"} size="lg" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.4em] text-white/45">{editing.id ? "Edit profile" : "New profile"}</p>
            <h2 className="font-display font-bold text-2xl tracking-tighter mt-1">
              {editing.name?.trim() || "Untitled"}
            </h2>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/45">Name</label>
            <input
              type="text"
              maxLength={30}
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              className="mt-1 w-full h-11 bg-black/40 border border-white/15 px-3 text-sm focus:outline-none focus:border-white"
              data-testid="profile-editor-name"
              autoFocus
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/45">Avatar</label>
            <div className="mt-2 grid grid-cols-5 gap-3">
              {AVATAR_OPTIONS.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => setEditing({ ...editing, avatar_key: a.key })}
                  className="flex flex-col items-center gap-1.5"
                  data-testid={`avatar-option-${a.key}`}
                >
                  <ProfileAvatar avatarKey={a.key} name={editing.name || "?"} size="md" active={editing.avatar_key === a.key} />
                  <span className="text-[9px] uppercase tracking-[0.2em] text-white/40">{a.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between border border-white/10 px-4 py-3">
            <div>
              <div className="text-sm font-medium">Kids profile</div>
              <div className="text-[11px] text-white/55">Restricts catalogue to U / U/A 7+ content.</div>
            </div>
            <Switch
              checked={!!editing.is_kid}
              onCheckedChange={(v) => setEditing({ ...editing, is_kid: v })}
              data-testid="profile-editor-is-kid"
            />
          </div>

          <div className="border border-white/10 px-4 py-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">PIN lock</div>
              {editing.hasPin && !editing.clearPin && (
                <button
                  type="button"
                  onClick={() => setEditing({ ...editing, clearPin: true, pin: "" })}
                  className="text-[10px] uppercase tracking-[0.25em] text-rose-300 hover:text-rose-200"
                  data-testid="profile-editor-clear-pin"
                >
                  Remove PIN
                </button>
              )}
              {editing.clearPin && (
                <span className="text-[10px] uppercase tracking-[0.25em] text-rose-300" data-testid="profile-editor-clearpin-pending">
                  Pending removal
                </span>
              )}
            </div>
            <p className="text-[11px] text-white/55">{editing.hasPin ? "Already set. Type below to replace it." : "Optional 4-digit lock for this profile."}</p>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              pattern="\d{4}"
              placeholder={editing.hasPin ? "••••" : "1234"}
              value={editing.pin || ""}
              onChange={(e) => setEditing({ ...editing, pin: e.target.value.replace(/\D/g, "").slice(0, 4), clearPin: false })}
              className="w-full h-11 bg-black/40 border border-white/15 px-3 text-sm tracking-[0.5em] focus:outline-none focus:border-white"
              data-testid="profile-editor-pin"
            />
          </div>

          {error && (
            <div className="text-[11px] uppercase tracking-[0.25em] text-rose-300" data-testid="profile-editor-error">{error}</div>
          )}
          {!editing.id && totalProfiles >= MAX_PROFILES && (
            <div className="text-[11px] uppercase tracking-[0.25em] text-rose-300">Maximum 5 profiles reached.</div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting || (!editing.id && totalProfiles >= MAX_PROFILES)}
              className="flex-1 h-12 font-bold uppercase tracking-[0.25em] text-xs disabled:opacity-50"
              style={{ backgroundColor: accentColor, color: "#0a0a0a" }}
              data-testid="profile-editor-save"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (editing.id ? "Save changes" : "Create profile")}
            </button>
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="h-12 px-5 border border-rose-500/40 text-rose-300 hover:border-rose-300 hover:text-rose-200 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em]"
                data-testid="profile-editor-delete"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            )}
          </div>
        </div>
        <div className="absolute -top-px left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${themeColor}, transparent)` }} />
      </div>
    </div>
  );
}

function ConfirmDelete({ profile, onCancel, onConfirm, submitting }) {
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/85 p-4" onClick={onCancel} data-testid="profile-confirm-delete">
      <div className="bg-[#0a0a0a] border border-white/10 max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-xl tracking-tight">Delete {profile.name}?</h3>
        <p className="mt-2 text-white/60 text-sm font-light">
          This profile and its watch history will be permanently removed. The account remains.
        </p>
        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 h-11 border border-white/20 text-[11px] uppercase tracking-[0.25em] hover:border-white"
            data-testid="profile-delete-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 h-11 bg-rose-600 hover:bg-rose-500 text-white font-bold text-[11px] uppercase tracking-[0.25em] disabled:opacity-50"
            data-testid="profile-delete-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}
